/**
 * Achtergrondjob "AI voorbereiden" (tool-feedback #63): starten, pollen, stoppen en na een
 * refresh/paginawissel weer oppakken. Per deel dat klaar is (callsheet, briefing) roept de
 * hook `onDeel` aan na een tijdelijke claim bij de server (één tabblad tegelijk); daarna
 * `klaar` (nooit meer oppakken) of, als `onDeel` false teruggeeft of faalt, `vrij` (later
 * opnieuw proberen). Herhaalde vragen ('opnieuw') vullen niets zelf in: die gaan na afloop
 * via `onKlaar` naar de voorsteldialoog.
 *
 * Alles is gebonden aan een generatie: wissel je naar een andere callsheet, dan wordt een laat
 * antwoord van de vorige nooit met de callbacks van de nieuwe verwerkt (Codex r1 #3).
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const POLL_MS = 1500;
const POLL_FOUT_MS = 4000;
const MAX_POGINGEN = 3;
const HERPROBEER_MS = 15000;     // afgeronde job met een deel dat (nog) niet te claimen was
const MAX_HERPROBEER = 24;       // ± 6 min: langer dan een claim geldig is (CLAIM_S in de engine)
const HERVAT_MIN = 30;           // na een refresh: afgeronde job met open voorstellen nog tonen
const DELEN = ['callsheet', 'briefing'];
const JSON_KOP = { 'Content-Type': 'application/json' };

export default function useAiVoorbereiden({ callsheetId, fetcher, base, onDeel, onKlaar }) {
  const [job, setJob] = useState(null);
  const [starten, setStarten] = useState(false);
  const cb = useRef({ onDeel, onKlaar });
  cb.current = { onDeel, onKlaar };
  const gen = useRef(0);
  const csRef = useRef(callsheetId);
  const gezien = useRef(new Set());       // `${jobId}:${deel}` die deze tab nu behandelt of heeft behandeld
  const pogingen = useRef({});
  const klaarGemeld = useRef(new Set());
  const keten = useRef(Promise.resolve());
  const timer = useRef(null);
  const huidigJob = useRef(null);

  const pad = useCallback((cs, p = '') => `${base}/Api/callsheets/${cs}/ai-voorbereiden${p}`, [base]);
  const actueel = (g, j) => g === gen.current && (!j || (j.callsheetId || csRef.current) === csRef.current);

  const herprobeer = useRef({});
  const meld = useCallback(async (cs, jobId, deel, actie, token) => {
    const r = await fetcher(pad(cs, `/${jobId}/verwerkt`), { method: 'POST', headers: JSON_KOP, body: JSON.stringify({ deel, actie, ...(token ? { token } : {}) }) });
    return r.ok ? r.json().catch(() => ({})) : {};
  }, [fetcher, pad]);

  const poll = useRef(null);

  const verwerk = useCallback(async (j, g) => {
    if (!actueel(g, j)) return;
    const cs = csRef.current;
    const delen = j?.resultaat?.delen || {};
    let open = false;   // een deel dat nog moet en nu niet lukte
    for (const deel of DELEN) {
      const sleutel = `${j.jobId}:${deel}`;
      if (!delen[deel] || gezien.current.has(sleutel)) continue;
      gezien.current.add(sleutel);
      if (j.opnieuw || (j.verwerkt || []).includes(deel)) continue;
      let claim = null;
      try { claim = await meld(cs, j.jobId, deel, 'claim'); } catch { claim = null; }
      if (!actueel(g, j)) { gezien.current.delete(sleutel); return; }
      if (!claim?.geclaimd) { gezien.current.delete(sleutel); open = true; continue; }   // later opnieuw (claim kan verlopen)
      let gelukt = false;
      try {
        gelukt = (await cb.current.onDeel?.(deel, delen[deel], j, { actueel: () => actueel(g, j) })) !== false;
      } catch (e) { console.error('[AI voorbereiden] invullen', e); }
      try { await meld(cs, j.jobId, deel, gelukt ? 'klaar' : 'vrij', claim.token); } catch { /* claim verloopt vanzelf */ }
      if (!gelukt) {
        gezien.current.delete(sleutel);
        pogingen.current[sleutel] = (pogingen.current[sleutel] || 0) + 1;
        if (pogingen.current[sleutel] < MAX_POGINGEN) open = true;
      }
      if (!actueel(g, j)) return;
    }
    // Afgeronde job met een deel dat nog moet: blijven proberen tot het lukt of de pogingen op zijn
    // (een bezige job wordt toch al gepold).
    if (open && j.status !== 'bezig' && huidigJob.current === j.jobId) {
      const n = (herprobeer.current[j.jobId] || 0) + 1;
      herprobeer.current[j.jobId] = n;
      if (n <= MAX_HERPROBEER) {
        clearTimeout(timer.current);
        timer.current = setTimeout(() => poll.current?.(j.jobId, g), n === 1 ? POLL_FOUT_MS : HERPROBEER_MS);
      }
    }
    if (j.status !== 'bezig' && !klaarGemeld.current.has(j.jobId) && actueel(g, j)) {
      klaarGemeld.current.add(j.jobId);
      cb.current.onKlaar?.(j);
    }
  }, [meld]); // eslint-disable-line react-hooks/exhaustive-deps

  // Verwerken strikt na elkaar: onKlaar komt pas als de delen zijn ingevuld.
  const zetInRij = useCallback((j, g) => {
    keten.current = keten.current.then(() => verwerk(j, g)).catch(() => {});
    return keten.current;
  }, [verwerk]);

  poll.current = async (jobId, g) => {
    clearTimeout(timer.current);
    if (!actueel(g) || huidigJob.current !== jobId) return;
    try {
      const r = await fetcher(pad(csRef.current, `/${jobId}`));
      if (!actueel(g) || huidigJob.current !== jobId) return;
      if (r.status === 404) { setJob(null); return; }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      if (!actueel(g, j) || huidigJob.current !== jobId) return;
      setJob(j);
      zetInRij(j, g);
      if (j.status === 'bezig') timer.current = setTimeout(() => poll.current(jobId, g), POLL_MS);
    } catch {
      if (actueel(g)) timer.current = setTimeout(() => poll.current(jobId, g), POLL_FOUT_MS);
    }
  };

  const volg = useCallback((j, g, { hervat = false } = {}) => {
    if (!actueel(g, j)) return;
    huidigJob.current = j.jobId;
    setJob(hervat ? { ...j, hervat: true } : j);
    zetInRij(j, g);
    if (j.status === 'bezig') timer.current = setTimeout(() => poll.current(j.jobId, g), POLL_MS);
  }, [zetInRij]); // eslint-disable-line react-hooks/exhaustive-deps

  // Nieuwe callsheet (of eerste keer): nieuwe generatie; lopende (of klaar maar nog niet
  // ingevulde) job van deze callsheet oppakken — ook na een refresh.
  useEffect(() => {
    gen.current += 1;
    const g = gen.current;
    csRef.current = callsheetId;
    huidigJob.current = null;
    keten.current = Promise.resolve();
    clearTimeout(timer.current);
    setJob(null);
    if (!callsheetId) return undefined;
    (async () => {
      try {
        const r = await fetcher(pad(callsheetId));
        if (!r.ok || !actueel(g)) return;
        const { job: j } = await r.json();
        if (!j || !actueel(g, j)) return;
        const open = DELEN.some((d) => j.resultaat?.delen?.[d] && !(j.verwerkt || []).includes(d));
        // Voorstellen bij gevulde velden staan niet op het callsheet: na een refresh nog even tonen.
        const recent = j.gestart && Date.now() - Date.parse(j.gestart) < HERVAT_MIN * 60000;
        const voorstellen = DELEN.some((d) => (j.resultaat?.delen?.[d]?.voorstellen || []).length);
        if (j.status === 'bezig' || (!j.opnieuw && (open || (recent && voorstellen)))) volg(j, g, { hervat: j.status !== 'bezig' });
      } catch { /* geen lopende voorbereiding */ }
    })();
    return () => { gen.current += 1; clearTimeout(timer.current); };
  }, [callsheetId]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = useCallback(async (body = {}) => {
    const g = gen.current;
    setStarten(true);
    try {
      const r = await fetcher(pad(csRef.current), { method: 'POST', headers: JSON_KOP, body: JSON.stringify(body) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.detail || j.error || `AI voorbereiden mislukt (HTTP ${r.status})`);
      clearTimeout(timer.current);
      volg(j, g);
      return j;
    } finally {
      setStarten(false);
    }
  }, [fetcher, pad, volg]);

  const stop = useCallback(async () => {
    const id = huidigJob.current;
    const g = gen.current;
    if (!id) return;
    setJob((j) => (j ? { ...j, stopGevraagd: true } : j));
    try {
      const r = await fetcher(pad(csRef.current, `/${id}/stop`), { method: 'POST' });
      if (r.ok && actueel(g)) { const j = await r.json(); if (actueel(g, j)) { setJob(j); zetInRij(j, g); } }
    } catch { /* de volgende poll laat de stand zien */ }
  }, [fetcher, pad, zetInRij]); // eslint-disable-line react-hooks/exhaustive-deps

  const sluit = useCallback(() => { clearTimeout(timer.current); huidigJob.current = null; setJob(null); }, []);

  return { job, bezig: starten || job?.status === 'bezig', starten, start, stop, sluit };
}
